/**
 * @file ProductMainPanels.tsx
 * @description Панели элементов товара первой колонки шаблона (phase19.16
 * Р2а; uiux-design-r2a §3; SB34, SB37): «Галерея» (миниатюры под фото /
 * сбоку / сеткой, увеличение), «Название» (H1, рейтинг под названием),
 * «Цена и покупка» (фасовки кнопками или списком, количество, ширина
 * кнопки). Без настроек — «Хлебные крошки», «Бейджи», «Короткое описание»:
 * только строка, откуда данные; «Расположение» — общей группой ниже.
 * SB120 этап 3б: раскладка фото схемами, + «Справа» (`GalleryLayoutField`).
 */

import { Label } from "../../../../ui/label";
import { CheckRow } from "../../../../site-view/CheckRow";
import { Segmented } from "../../../../site-view/Segmented";
import type { ElementPanelProps } from "../types";
import { GalleryLayoutField } from "./GalleryLayoutField";
import { ProductNote } from "./ProductNote";

export function ProductGalleryPanel({ element, onChange }: ElementPanelProps<"product-gallery">) {
  return (
    <>
      <GalleryLayoutField value={element.layout} onChange={(layout) => onChange({ ...element, layout })} />
      <CheckRow checked={element.zoom} onChange={(zoom) => onChange({ ...element, zoom })}>
        Открывать фото крупно по нажатию
      </CheckRow>
      <ProductNote>Фото берутся из карточки товара. Нет фото — на сайте заглушка.</ProductNote>
    </>
  );
}

export function ProductTitlePanel({ element, onChange }: ElementPanelProps<"product-title">) {
  return (
    <>
      <ProductNote>Название товара — главный заголовок страницы (H1). Других H1 в шаблоне не нужно.</ProductNote>
      <CheckRow checked={element.showRating} onChange={(showRating) => onChange({ ...element, showRating })}>
        Звёзды и число отзывов под названием
      </CheckRow>
      <p className="type-caption text-on-surface-variant">Рейтинг появляется у товара с трёх отзывов.</p>
    </>
  );
}

const SKU_PICKER = [
  { value: "chips" as const, label: "Кнопками" },
  { value: "select" as const, label: "Списком" },
];

const BUTTON_WIDTH = [
  { value: "full" as const, label: "Во всю ширину" },
  { value: "auto" as const, label: "По тексту" },
];

export function ProductBuyPanel({ element, onChange }: ElementPanelProps<"product-buy">) {
  return (
    <>
      <Label>Фасовки</Label>
      <Segmented aria-label="Как показать фасовки" options={SKU_PICKER} value={element.skuPicker} onChange={(skuPicker) => onChange({ ...element, skuPicker })} />
      {element.skuPicker === "chips" ? <p className="type-caption text-on-surface-variant">Если фасовок больше шести — удобнее «Списком».</p> : null}
      <CheckRow checked={element.showQuantity} onChange={(showQuantity) => onChange({ ...element, showQuantity })}>
        Выбор количества
      </CheckRow>
      <Label>Кнопка «В корзину»</Label>
      <Segmented aria-label="Ширина кнопки" options={BUTTON_WIDTH} value={element.buttonWidth} onChange={(buttonWidth) => onChange({ ...element, buttonWidth })} />
      <ProductNote>
        Цена, старая цена и фасовки — из товара. Старая цена видна, только когда цена правда снижена: прежняя действовала не меньше 30 дней. У товара «Скоро в продаже» кнопка неактивна.
      </ProductNote>
    </>
  );
}

export function BreadcrumbsPanel(_props: ElementPanelProps<"breadcrumbs">) {
  return <ProductNote>Главная › Каталог › категория › товар — строится сама.</ProductNote>;
}

export function ProductBadgesPanel(_props: ElementPanelProps<"product-badges">) {
  return (
    <ProductNote>Бейджи товара: новинка, бестселлер, скидка, «скоро в продаже». Подписи бейджей — в «Сайт → Каталог → Карточка товара».</ProductNote>
  );
}

export function ProductShortPanel(_props: ElementPanelProps<"product-short">) {
  return <ProductNote>Короткое описание (подзаголовок) — из карточки товара. Пусто у товара — на его странице элемента не будет.</ProductNote>;
}
